import { FINISHING_ICON } from './Icon';
import { ORDER_STAGES } from '../constants';
import type { Order, OrderStage, PaymentStatus, Urgency } from '../types';
import {
  allOrderLineItemsComplete,
  allOrderLineItemsDelivered,
  allOrderLineItemsPastFulfillmentAndDispatch,
  lineItemsWithDispatchInWorkflow,
} from '../utils/lineItemWorkflowHelpers';
import { getOrderStageLabel } from '../utils/stageHelpers';
import { getActiveStep, workflowStepDetailTitle } from '../utils/workflowHelpers';

function BadgeIcon({ d }: { d: readonly string[] | string }) {
  const paths = Array.isArray(d) ? d : [d];
  return (
    <svg
      className="badge-icon"
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths.map((p, i) => (
        <path key={i} d={p} />
      ))}
    </svg>
  );
}

const BADGE_ICONS = {
  critical: ['M12 9v4', 'M12 17h.01', 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'],
  urgent: ['M12 2a10 10 0 100 20A10 10 0 0012 2z', 'M12 6v6l4 2'],
  normal: 'M12 2a10 10 0 100 20A10 10 0 0012 2z',
  paid: ['M9 12l2 2 4-4', 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z'],
  partial: ['M12 2a10 10 0 100 20A10 10 0 0012 2z', 'M12 2v20'],
  unpaid: ['M15 9l-6 6', 'M9 9l6 6', 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z'],
  stageReceived: ['M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z', 'M3.27 6.96L12 12.01l8.73-5.05', 'M12 22.08V12'],
  stageInvoice: ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8'],
  stageProduction: ['M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', 'M15 12a3 3 0 11-6 0 3 3 0 016 0z'],
  stageDesign: ['M3 21l19-11L12 3 3 21l9-4 9 4', 'M9.5 14.5l-3 3'],
  stageFinishing: FINISHING_ICON,
  stageQc: ['M9 12l2 2 4-4', 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z'],
  stageQcFailed: ['M12 9v4', 'M12 17h.01', 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z'],
  stageDispatch: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z', 'M18.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z'],
  stageDelivered: ['M22 11.08V12a10 10 0 11-5.93-9.14', 'M22 4L12 14.01l-3-3'],
  stageHold: ['M10 4H6v16h4V4z', 'M18 4h-4v16h4V4z'],
  stageCancelled: ['M18 6L6 18', 'M6 6l12 12'],
  stageDefault: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2',
} as const;

export const URGENCY_ICONS = {
  normal: BADGE_ICONS.normal,
  urgent: BADGE_ICONS.urgent,
  critical: BADGE_ICONS.critical,
} as const;

export function StageBadge({
  stage,
  order,
}: {
  stage: OrderStage;
  order?: Pick<Order, 'stage' | 'workflow' | 'currentStepIndex' | 'stageHistory' | 'lineItems' | 'deliveryType'>;
}) {
  const displayStage: OrderStage =
    order && allOrderLineItemsDelivered(order) ? 'delivered' : stage;
  const label = order ? getOrderStageLabel(order) : ORDER_STAGES.find((s) => s.id === stage)?.label ?? stage;
  const fulfillmentComplete =
    order &&
    allOrderLineItemsPastFulfillmentAndDispatch(order) &&
    !allOrderLineItemsDelivered(order);
  const awaitingDispatch =
    order &&
    allOrderLineItemsComplete(order) &&
    !allOrderLineItemsPastFulfillmentAndDispatch(order) &&
    lineItemsWithDispatchInWorkflow(order).length > 0;
  const cls = fulfillmentComplete
    ? 'badge-completed'
    : awaitingDispatch
      ? 'badge-stage-dispatch'
      : stageClass(displayStage);
  const activeStep =
    order &&
    displayStage === 'in_fulfillment' &&
    (order.stage === 'in_fulfillment' || order.stage === 'on_hold')
      ? getActiveStep(order as Order)
      : undefined;
  const title = activeStep ? workflowStepDetailTitle(activeStep) : undefined;
  return (
    <span className={`badge ${cls}`} title={title}>
      <BadgeIcon d={stageIcon(displayStage)} />
      {label}
    </span>
  );
}

export function PaymentBadge({ status }: { status: PaymentStatus }) {
  const map: Record<PaymentStatus, { cls: string; label: string; icon: readonly string[] | string }> = {
    paid:    { cls: 'badge-paid',    label: 'Paid',    icon: BADGE_ICONS.paid },
    partial: { cls: 'badge-partial', label: 'Partial', icon: BADGE_ICONS.partial },
    unpaid:  { cls: 'badge-unpaid',  label: 'Unpaid',  icon: BADGE_ICONS.unpaid },
  };
  const { cls, label, icon } = map[status];
  return (
    <span className={`badge ${cls}`}>
      <BadgeIcon d={icon} />
      {label}
    </span>
  );
}

export function CompletedBadge({
  compact,
  title,
}: {
  compact?: boolean;
  title?: string;
}) {
  return (
    <span
      className={`badge badge-completed${compact ? ' badge-completed--compact' : ''}`}
      title={title}
    >
      <BadgeIcon d={BADGE_ICONS.paid} />
      Completed
    </span>
  );
}

export function UrgencyBadge({ urgency }: { urgency: Urgency }) {
  if (urgency === 'normal') return null;
  const isCritical = urgency === 'critical';
  return (
    <span className={`badge ${isCritical ? 'badge-critical' : 'badge-urgent'}`}>
      <BadgeIcon d={isCritical ? BADGE_ICONS.critical : BADGE_ICONS.urgent} />
      {isCritical ? 'Critical' : 'Urgent'}
    </span>
  );
}

export function DraftBadge() {
  return (
    <span className="badge badge-stage-draft">
      <BadgeIcon d={['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8']} />
      Draft
    </span>
  );
}

function stageIcon(stage: OrderStage): readonly string[] | string {
  if (stage === 'draft') return BADGE_ICONS.stageDefault;
  if (stage === 'order_received') return BADGE_ICONS.stageReceived;
  if (stage === 'pending_invoice' || stage === 'invoice_assigned') return BADGE_ICONS.stageInvoice;
  if (stage === 'in_fulfillment') return BADGE_ICONS.stageProduction;
  if (stage === 'ready_for_dispatch' || stage === 'dispatched') return BADGE_ICONS.stageDispatch;
  if (stage === 'delivered') return BADGE_ICONS.stageDelivered;
  if (stage === 'on_hold') return BADGE_ICONS.stageHold;
  if (stage === 'cancelled') return BADGE_ICONS.stageCancelled;
  return BADGE_ICONS.stageDefault;
}

function stageClass(stage: OrderStage): string {
  if (stage === 'draft') return 'badge-stage-draft';
  if (stage === 'order_received')   return 'badge-stage-received';
  if (stage === 'pending_invoice')  return 'badge-stage-invoice';
  if (stage === 'invoice_assigned') return 'badge-stage-invoice';
  if (stage === 'in_fulfillment')   return 'badge-stage-production';
  if (stage === 'ready_for_dispatch' || stage === 'dispatched') return 'badge-stage-dispatch';
  if (stage === 'delivered')        return 'badge-stage-delivered';
  if (stage === 'on_hold')          return 'badge-stage-hold';
  if (stage === 'cancelled')        return 'badge-stage-cancelled';
  return 'badge-stage';
}
